import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, KeyRound, DollarSign, Phone, Rocket, Zap, CalendarCheck, UserPlus, Play, Star, Check, X } from 'lucide-react';
import { EditCtx, PageCtx, Sec, Tx, Cta, Editable, VideoFrame, Ext } from './EditableBits';

const BULLET_ICONS = [Bot, KeyRound, DollarSign, Phone, Rocket, Zap];

const CONVO = [
  { role: 'lead', text: 'Hey — saw your ad. Do you do kitchen renovations?' },
  { role: 'ai', text: 'We do — full kitchen remodels are our specialty. Rough idea of your budget and timeline?' },
  { role: 'lead', text: 'Around $25k, hoping to start next month.' },
  { role: 'ai', text: 'Perfect fit. I have Thursday 2pm open for a free on-site quote — should I lock it in?' },
  { role: 'lead', text: 'Yes please!' },
  { role: 'outcome', text: 'Appointment booked · synced to CRM' },
];

/* ── product visual cards (same language as the main site hero) ── */
const CallCard = () => (
  <div className="w-72 rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-xl p-5 shadow-lg" data-testid="lander-call-card">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-2 h-2 rounded-full bg-[#06B6D4] arc-live-dot" />
      <span className="arc-body text-xs font-bold uppercase tracking-[0.18em] text-[#141414]/60">Live call</span>
      <span className="arc-body text-sm font-semibold text-[#141414]/50 ml-auto">2m 14s</span>
    </div>
    <div className="flex items-end gap-[3px] h-9 mb-3">
      {[0.5, 0.9, 0.4, 1, 0.7, 0.3, 0.85, 0.55, 1, 0.45, 0.75, 0.35, 0.9, 0.6, 0.8, 0.5].map((h, i) => (
        <span key={i} className="arc-wave-bar w-[4px] rounded-full bg-[#141414]" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.08}s` }} />
      ))}
    </div>
    <div className="flex items-center justify-between gap-2">
      <span className="arc-body text-sm font-bold text-[#141414] whitespace-nowrap">Sam · AI Closer</span>
      <span className="arc-body text-xs font-bold text-[#F0EFEC] bg-[#141414] rounded-full px-3 py-1.5 whitespace-nowrap">Appointment booked</span>
    </div>
  </div>
);

const ChatCard = () => (
  <div className="w-72 rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-xl p-5 shadow-lg" data-testid="lander-chat-card">
    <div className="flex items-center gap-2 mb-3">
      <Zap size={13} className="text-[#06B6D4]" />
      <span className="arc-body text-xs font-bold uppercase tracking-[0.18em] text-[#141414]/60">Instant Inbox</span>
      <span className="arc-body text-sm font-semibold text-[#141414]/50 ml-auto">WhatsApp</span>
    </div>
    <div className="space-y-2">
      <div className="arc-body max-w-[85%] rounded-2xl rounded-bl-md bg-[#141414]/[0.06] px-3.5 py-2.5 text-sm font-medium text-[#141414]">
        How much for a full detail?
      </div>
      <div className="arc-body max-w-[90%] ml-auto rounded-2xl rounded-br-md bg-[#141414] px-3.5 py-2.5 text-sm font-medium text-[#F0EFEC]">
        I can hold tomorrow 10am for you — want it?
      </div>
    </div>
    <div className="arc-body text-xs font-bold text-[#141414]/50 mt-2.5 text-right">replied in 3s</div>
  </div>
);

const CalendarCard = () => (
  <div className="w-72 rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-xl p-5 shadow-lg" data-testid="lander-calendar-card">
    <div className="flex items-center gap-2 mb-4">
      <CalendarCheck size={13} className="text-[#06B6D4]" />
      <span className="arc-body text-xs font-bold uppercase tracking-[0.18em] text-[#141414]/60">Calendar</span>
      <span className="arc-body text-sm font-semibold text-[#141414]/50 ml-auto">booked by AI</span>
    </div>
    <div className="flex items-center gap-4">
      <div className="rounded-xl border border-black/[0.08] bg-white overflow-hidden text-center shrink-0 w-14">
        <div className="arc-body text-[10px] font-bold uppercase tracking-widest bg-[#141414] text-[#F0EFEC] py-1">Thu</div>
        <div className="arc-head font-black text-xl text-[#141414] py-1.5">26</div>
      </div>
      <div className="min-w-0">
        <div className="arc-body text-sm font-bold text-[#141414] truncate">Kitchen remodel — on-site quote</div>
        <div className="arc-body text-sm text-[#141414]/55 mt-0.5">2:00 PM · Jess Miller</div>
      </div>
    </div>
  </div>
);

const ContactCard = () => (
  <div className="w-72 rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-xl p-5 shadow-lg" data-testid="lander-contact-card">
    <div className="flex items-center gap-2 mb-4">
      <UserPlus size={13} className="text-[#06B6D4]" />
      <span className="arc-body text-xs font-bold uppercase tracking-[0.18em] text-[#141414]/60">CRM update</span>
      <span className="arc-body text-sm font-semibold text-[#141414]/50 ml-auto">automatic</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="w-11 h-11 rounded-full bg-[#141414] flex items-center justify-center shrink-0">
        <span className="arc-body text-xs font-bold text-[#F0EFEC]">JM</span>
      </span>
      <div className="min-w-0">
        <div className="arc-body text-sm font-bold text-[#141414]">Jess Miller</div>
        <div className="flex gap-1.5 mt-1.5">
          <span className="arc-body text-[11px] font-bold text-[#141414] bg-[#141414]/[0.06] rounded-full px-2.5 py-1">Contact created</span>
          <span className="arc-body text-[11px] font-bold text-[#F0EFEC] bg-[#141414] rounded-full px-2.5 py-1">hot-lead</span>
        </div>
      </div>
    </div>
  </div>
);

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const useCountdown = (iso) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!iso) { setLeft(null); return; }
    const tick = () => {
      const diff = new Date(iso).getTime() - Date.now();
      if (diff <= 0) { setLeft(null); return; }
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return left;
};

const drift = (delay = 0, dist = 14, dur = 7) => ({
  animate: { y: [0, -dist, 0] },
  transition: { duration: dur, delay, repeat: Infinity, ease: 'easeInOut' },
});

const LiveConversation = () => {
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setVisible((v) => (v >= CONVO.length ? 1 : v + 1)), 1700);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 h-[420px] flex flex-col overflow-hidden" data-testid="lander-live-conversation">
      <div className="flex items-center gap-2 pb-4 mb-4 border-b border-white/[0.08] shrink-0">
        <Zap size={13} className="text-[#06B6D4]" />
        <span className="arc-body text-xs font-bold uppercase tracking-[0.2em] text-[#F0EFEC]/50">Live conversation</span>
        <span className="arc-body text-[11px] text-[#F0EFEC]/35 ml-auto">Jess · new lead</span>
      </div>
      <div className="flex-1 flex flex-col justify-end space-y-3 overflow-hidden min-h-0">
        <AnimatePresence initial={false}>
          {CONVO.slice(0, visible).map((msg, i) =>
            msg.role === 'outcome' ? (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="flex justify-center pt-2">
                <span className="arc-body inline-flex items-center gap-2 rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/10 px-4 py-2 text-xs font-bold text-[#F0EFEC]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]" />
                  {msg.text}
                </span>
              </motion.div>
            ) : (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className={msg.role === 'ai' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={msg.role === 'ai'
                  ? 'arc-body max-w-[80%] rounded-2xl rounded-br-md bg-[#F0EFEC] px-4 py-3 text-base font-medium text-[#141414]'
                  : 'arc-body max-w-[80%] rounded-2xl rounded-bl-md bg-white/[0.07] border border-white/[0.06] px-4 py-3 text-base font-medium text-[#F0EFEC]/90'}>
                  {msg.text}
                  {msg.role === 'ai' && <span className="block text-xs font-semibold text-[#141414]/50 mt-1.5">AI · replied in 3s</span>}
                </div>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const ReviewCard = ({ r }) => (
  <div className="w-[380px] shrink-0 flex flex-col rounded-2xl border border-black/[0.05] bg-white p-7 shadow-[0_2px_16px_rgba(20,20,20,0.04)]" data-testid="lander-review-card">
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => <Star key={i} size={15} className="text-[#F5A623]" fill="#F5A623" />)}
    </div>
    <p className="arc-body text-sm text-[#141414]/70 leading-relaxed flex-1">"{r.text}"</p>
    <div className="mt-6 pt-4 border-t border-black/[0.05] flex items-baseline gap-2 whitespace-nowrap overflow-hidden">
      <span className="arc-body text-sm font-bold text-[#141414]">{r.name}</span>
      <span className="arc-body text-xs text-[#141414]/40">· {r.role}</span>
    </div>
  </div>
);

/* ═══ BLOCKS ═══ */

export const CountdownBlock = ({ id, p }) => {
  const { config } = useContext(PageCtx);
  const left = useCountdown(config.event_start_iso);
  return (
    <Sec id={id} as="div" className="py-3 px-4" style={{ background: p.bg || '#141414', color: p.fg || '#F0EFEC' }} testid="lander-countdown-banner">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
        <Tx v={p.label} sec={id} k="label" as="span" base="arc-body text-xs sm:text-sm font-bold tracking-wide uppercase" strongClass="font-black" />
        {p.show_timer !== false && left && (
          <div className="flex items-center gap-2" data-testid="lander-countdown-timer">
            {[['Days', left.d], ['Hrs', left.h], ['Min', left.m], ['Sec', left.s]].map(([label, val]) => (
              <span key={label} className="flex flex-col items-center rounded-lg bg-white/[0.08] px-2.5 py-1 min-w-[46px]">
                <span className="arc-head font-black text-base leading-none tabular-nums">{pad(val)}</span>
                <span className="arc-body text-[9px] font-bold uppercase tracking-widest opacity-50 mt-0.5">{label}</span>
              </span>
            ))}
          </div>
        )}
        <Ext sec={id} p={p} at="end" />
      </div>
    </Sec>
  );
};

export const HeroBlock = ({ id, p }) => {
  const { config } = useContext(PageCtx);
  return (
    <Sec id={id} className="relative overflow-hidden pb-24 px-6" bg={p.bg}>
      <div className="arc-blob w-96 h-96 bg-[#D8D2C8]/70 -top-20 -left-24" />
      <div className="arc-blob w-80 h-80 bg-[#E0DACE]/80 top-1/3 right-[-6rem]" />

      {p.show_cards !== false && (
        <>
          <motion.div {...drift(0.3, 14, 7.5)} className="hidden xl:block absolute left-[3%] top-[10%] z-10 -rotate-2"><CallCard /></motion.div>
          <motion.div {...drift(1.2, 12, 6.5)} className="hidden xl:block absolute left-[4%] bottom-[16%] z-10 rotate-1"><CalendarCard /></motion.div>
          <motion.div {...drift(0.9, 13, 7)} className="hidden xl:block absolute right-[3%] top-[12%] z-10 rotate-2"><ChatCard /></motion.div>
          <motion.div {...drift(1.6, 14, 8)} className="hidden xl:block absolute right-[4%] bottom-[16%] z-10 -rotate-1"><ContactCard /></motion.div>
        </>
      )}

      <header className="flex items-center justify-center pt-8 mb-12 relative z-20">
        <img src="/voxinity-logo-light.png?v=8" alt="Voxinity AI" className="h-8 w-auto" />
      </header>

      <div className="max-w-4xl mx-auto text-center relative z-20">
        <Tx v={p.eyebrow} sec={id} k="eyebrow" base="arc-body text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#141414]/50 mb-7" testid="lander-audience-callout" />
        <Ext sec={id} p={p} at="eyebrow" />
        <Tx v={p.headline} sec={id} k="headline" as="h1" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl md:text-5xl xl:text-6xl leading-[1.06] mb-6" strongClass="font-black" testid="lander-headline" />
        <Ext sec={id} p={p} at="headline" />
        <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-lg md:text-xl font-medium text-[#141414]/70 max-w-2xl mx-auto mb-12" strongClass="font-bold text-[#141414]" testid="lander-subhead" />
        <Ext sec={id} p={p} at="subhead" />

        {p.show_video !== false && (
          <VideoFrame
            url={p.video_url || config.vsl_url}
            sec={id} k="video_url"
            className="max-w-3xl mx-auto mb-12 rounded-3xl overflow-hidden bg-[#141414] shadow-[0_28px_90px_rgba(20,20,20,0.28)]"
            testid="lander-vsl"
            placeholder={(
              <div className="aspect-video flex flex-col items-center justify-center" data-testid="lander-vsl-placeholder">
                <span className="w-20 h-20 rounded-full bg-[#F0EFEC] flex items-center justify-center mb-5 transition-transform hover:scale-105 cursor-pointer shadow-lg">
                  <Play size={26} className="text-[#141414] ml-1" fill="#141414" />
                </span>
                <span className="arc-body text-base font-bold text-white/80">Watch: how agencies are doing this right now</span>
                <span className="arc-body text-xs text-white/40 mt-1.5">2 min</span>
              </div>
            )}
          />
        )}
        <Ext sec={id} p={p} at="video_url" />

        <Cta cta={p.cta} sec={id} testid="lander-hero-cta" />
        <Ext sec={id} p={p} at="cta" />
        <Tx v={p.micro} sec={id} k="micro" base="arc-body text-sm text-[#141414]/45 mt-6" />
        <Ext sec={id} p={p} at="micro" />
        <Ext sec={id} p={p} at="end" />
      </div>

      {p.show_cards !== false && (
        <div className="xl:hidden relative z-10 mt-16 flex flex-col items-center gap-4">
          <CallCard />
          <ChatCard />
        </div>
      )}
    </Sec>
  );
};

export const StackBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 py-20 sm:py-24" bg={p.bg} testid="lander-stack-section">
    <div className="max-w-3xl mx-auto text-center">
      {p.show_icons !== false && (
        <div className="flex items-end justify-center mb-12" data-testid="lander-stack-icons">
          {[
            { rotate: -10, y: 14, node: <img src="/funnel/claude-logo.png" alt="Claude" className="w-full h-full object-cover" /> },
            { rotate: -4, y: 0, node: (
              <div className="w-full h-full bg-white flex items-center justify-center p-3">
                <img src="/funnel/ghl-logo.png" alt="GoHighLevel" className="w-full h-full object-contain" />
              </div>
            )},
            { rotate: 4, y: 0, node: (
              <div className="w-full h-full bg-[#141414] flex items-center justify-center">
                <img src="/funnel/voxinity-brain.png" alt="Voxinity AI" className="w-[88%] h-[88%] object-contain" />
              </div>
            )},
            { rotate: 10, y: 14, node: <img src="/funnel/calendar-icon.png" alt="Calendar" className="w-full h-full object-cover" /> },
          ].map((tile, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24, rotate: 0 }}
              whileInView={{ opacity: 1, y: tile.y, rotate: tile.rotate }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, type: 'spring', stiffness: 180, damping: 18 }}
              className="w-24 h-24 sm:w-36 sm:h-36 lg:w-40 lg:h-40 -mx-2 sm:-mx-3 shrink-0 rounded-[24%] overflow-hidden shadow-[0_24px_50px_rgba(20,20,20,0.28)] ring-1 ring-black/[0.06] bg-white"
              style={{ zIndex: i === 1 || i === 2 ? 2 : 1 }}
            >
              {tile.node}
            </motion.div>
          ))}
        </div>
      )}
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.body} sec={id} k="body" base="arc-body text-base sm:text-lg text-[#141414]/65 leading-relaxed max-w-2xl mx-auto mb-10" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="body" />
      <Cta cta={p.cta} sec={id} testid="lander-stack-cta" />
      <Ext sec={id} p={p} at="cta" />
      <Tx v={p.date_line} sec={id} k="date_line" base="arc-body text-sm text-[#141414]/45 mt-4" />
      <Ext sec={id} p={p} at="date_line" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const ConversationBlock = ({ id, p }) => (
  <Sec id={id} className="py-20 px-6" style={{ background: p.bg || '#141414' }} testid="lander-conversation-section">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Tx v={p.eyebrow} sec={id} k="eyebrow" base="arc-body text-xs font-bold uppercase tracking-[0.25em] text-[#F0EFEC]/45 mb-4" />
        <Ext sec={id} p={p} at="eyebrow" />
        <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#F0EFEC] text-3xl sm:text-4xl leading-tight mb-7" strongClass="font-black" />
        <Ext sec={id} p={p} at="headline" />
        <Tx v={p.para1} sec={id} k="para1" base="arc-body text-lg leading-relaxed text-[#F0EFEC]/70 max-w-md mb-5" strongClass="font-bold text-[#F0EFEC]" />
        <Ext sec={id} p={p} at="para1" />
        <Tx v={p.para2} sec={id} k="para2" base="arc-body text-lg leading-relaxed text-[#F0EFEC]/70 max-w-md" strongClass="font-bold text-[#F0EFEC]" />
        <Ext sec={id} p={p} at="para2" />
        <Ext sec={id} p={p} at="end" />
      </div>
      <LiveConversation />
    </div>
  </Sec>
);

export const BulletsBlock = ({ id, p }) => (
  <Sec id={id} className="py-20 px-6" bg={p.bg} testid="lander-bullets-section">
    <div className="max-w-3xl mx-auto">
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight text-center mb-12" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <div className="space-y-3.5">
        {(p.items || []).map((b, i) => {
          const Icon = BULLET_ICONS[i % BULLET_ICONS.length];
          return (
            <div key={i} className="flex items-start gap-4 rounded-2xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid={`lander-bullet-${i}`}>
              <span className="w-11 h-11 rounded-2xl bg-[#141414] flex items-center justify-center shrink-0">
                <Icon size={17} className="text-[#F0EFEC]" />
              </span>
              <p className="arc-body text-base text-[#141414]/65 leading-relaxed">
                <Tx v={b.title} sec={id} k={`items.${i}.title`} as="span" base="font-bold text-[#141414]" />
                {' — '}
                <Tx v={b.desc} sec={id} k={`items.${i}.desc`} as="span" base="" />
              </p>
            </div>
          );
        })}
      </div>
      <Ext sec={id} p={p} at="items" />
      <div className="text-center mt-10">
        <Cta cta={p.cta} sec={id} testid="lander-bullets-cta" />
        <Ext sec={id} p={p} at="cta" />
        <Ext sec={id} p={p} at="end" />
      </div>
    </div>
  </Sec>
);

export const FitBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pb-20" bg={p.bg} testid="lander-fit-section">
    <div className="max-w-5xl mx-auto">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" strongClass="font-black" />
        <Ext sec={id} p={p} at="headline" />
        <Tx v={p.subhead} sec={id} k="subhead" base="arc-body text-base sm:text-lg text-[#141414]/65 leading-relaxed" strongClass="font-bold text-[#141414]" />
        <Ext sec={id} p={p} at="subhead" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-7 sm:p-9" data-testid="lander-fit-for-card">
          <Tx v={p.for_title} sec={id} k="for_title" as="h3" base="arc-head font-black text-[#141414] text-xl sm:text-2xl mb-7" />
          <div className="space-y-6">
            {(p.for_items || []).map((item, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`lander-fit-for-${i}`}>
                <span className="w-9 h-9 rounded-xl bg-[#141414] flex items-center justify-center shrink-0">
                  <Check size={16} strokeWidth={3} className="text-[#F0EFEC]" />
                </span>
                <p className="arc-body text-base text-[#141414]/65 leading-relaxed">
                  <Tx v={item.text} sec={id} k={`for_items.${i}.text`} as="span" base="" strongClass="font-bold text-[#141414]" />
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-[#141414]/[0.03] border border-black/[0.06] p-7 sm:p-9" data-testid="lander-fit-notfor-card">
          <Tx v={p.notfor_title} sec={id} k="notfor_title" as="h3" base="arc-head font-black text-[#141414] text-xl sm:text-2xl mb-7" />
          <div className="space-y-6">
            {(p.notfor_items || []).map((item, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`lander-fit-notfor-${i}`}>
                <span className="w-9 h-9 rounded-xl bg-[#141414]/[0.08] flex items-center justify-center shrink-0">
                  <X size={16} strokeWidth={3} className="text-[#141414]/40" />
                </span>
                <p className="arc-body text-base text-[#141414]/55 leading-relaxed">
                  <Tx v={item.text} sec={id} k={`notfor_items.${i}.text`} as="span" base="" strongClass="font-bold text-[#141414]" />
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="text-center mt-12">
        <Cta cta={p.cta} sec={id} testid="lander-fit-cta" />
        <Ext sec={id} p={p} at="cta" />
        <Tx v={p.date_line} sec={id} k="date_line" base="arc-body text-sm text-[#141414]/45 mt-4" />
        <Ext sec={id} p={p} at="date_line" />
        <Ext sec={id} p={p} at="end" />
      </div>
    </div>
  </Sec>
);

export const AuthorityBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pb-16 pt-4" bg={p.bg} testid="lander-authority-section">
    <div className="max-w-2xl mx-auto text-center">
      <Tx v={p.eyebrow} sec={id} k="eyebrow" base="arc-body text-xs font-bold uppercase tracking-[0.25em] text-[#141414]/40 mb-4" />
      <Ext sec={id} p={p} at="eyebrow" />
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#141414] text-3xl sm:text-4xl leading-tight mb-6" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.body} sec={id} k="body" base="arc-body text-base text-[#141414]/65 leading-relaxed" strongClass="font-bold text-[#141414]" />
      <Ext sec={id} p={p} at="body" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const ReviewsBlock = ({ id, p }) => (
  <Sec id={id} className="pb-20 overflow-hidden" bg={p.bg} testid="lander-reviews-section">
    <div className="space-y-4">
      <Editable sec={id} k="rows_top">
        <div className="overflow-hidden">
          <div className="flex gap-4 w-max arc-marquee-track-rev" data-testid="lander-reviews-row-top">
            {[...(p.rows_top || []), ...(p.rows_top || [])].map((r, i) => <ReviewCard key={i} r={r} />)}
          </div>
        </div>
      </Editable>
      <Editable sec={id} k="rows_bottom">
        <div className="overflow-hidden">
          <div className="flex gap-4 w-max arc-marquee-track" data-testid="lander-reviews-row-bottom">
            {[...(p.rows_bottom || []), ...(p.rows_bottom || [])].map((r, i) => <ReviewCard key={i} r={r} />)}
          </div>
        </div>
      </Editable>
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);

export const FinalCtaBlock = ({ id, p }) => (
  <Sec id={id} className="px-6 pb-20" testid="lander-final-cta">
    <div className="max-w-4xl mx-auto rounded-3xl px-8 py-14 sm:py-16 text-center" style={{ background: p.bg || '#141414' }}>
      <Tx v={p.headline} sec={id} k="headline" as="h2" base="arc-head font-black text-[#F0EFEC] text-3xl sm:text-4xl leading-tight mb-4" strongClass="font-black" />
      <Ext sec={id} p={p} at="headline" />
      <Tx v={p.body} sec={id} k="body" base="arc-body text-base text-[#F0EFEC]/60 mb-9" strongClass="font-bold text-[#F0EFEC]" />
      <Ext sec={id} p={p} at="body" />
      <Cta cta={p.cta} sec={id} testid="lander-final-cta-btn" />
      <Ext sec={id} p={p} at="cta" />
      <Ext sec={id} p={p} at="end" />
    </div>
  </Sec>
);
